import { useCallback, useState } from 'react'
import { ZodError } from 'zod'
import {
  type Edge,
  type Node,
  getConnectedEdges,
  useReactFlow,
} from '@xyflow/react'
import {
  useFlowStore,
  type FlowValidationResult,
  type NodeValidationError,
} from '@/stores/flow-store'
import { NODES_METADATA } from '@/features/flow/_builder/components/blocks'
import {
  BuilderNode,
  BuilderNodeType,
} from '@/features/flow/_builder/components/blocks/types'

function findEdges(node: Node, connectedEdges: Edge[]) {
  const outgoingEdges = connectedEdges.filter((edge) => edge.source === node.id)
  const incomingEdges = connectedEdges.filter((edge) => edge.target === node.id)
  return { outgoingEdges, incomingEdges }
}

function checkFlowTerminalNode(
  outgoingEdges: Edge[],
  incomingEdges: Edge[],
  type: string
) {
  switch (type) {
    case BuilderNode.START:
      return outgoingEdges.length >= 1
    case BuilderNode.END:
      return incomingEdges.length >= 1
    default:
      return false
  }
}

function checkLoneNode(
  outgoingEdges: Edge[],
  incomingEdges: Edge[],
  type: string
) {
  switch (type) {
    case BuilderNode.START:
      return outgoingEdges.length === 0
    default:
      return incomingEdges.length === 0
  }
}

function validateNodeData(node: Node): NodeValidationError[] {
  const errors: NodeValidationError[] = []
  const nodeMetadata = NODES_METADATA[node.type as BuilderNodeType]

  if (!nodeMetadata?.validationSchema) {
    return errors
  }

  try {
    nodeMetadata.validationSchema.parse(node.data)
  } catch (error) {
    if (error instanceof ZodError) {
      for (const issue of error.issues) {
        errors.push({
          nodeId: node.id,
          nodeType: node.type || 'unknown',
          field: issue.path.join('.'),
          message: issue.message,
        })
      }
    }
  }

  return errors
}

export function useFlowValidator(
  onValidate?: (isValid: boolean, result?: FlowValidationResult) => void
): [boolean, () => void] {
  const [isValidating, setIsValidating] = useState(false)
  const { getNodes, getEdges } = useReactFlow()
  const {
    actions: { validation: validationActions },
  } = useFlowStore()

  const validate = useCallback(async () => {
    setIsValidating(true)

    await new Promise((resolve) => setTimeout(resolve, 300))

    const nodes = getNodes()
    const edges = getEdges()
    const connectedEdges = getConnectedEdges(nodes, edges)

    const validationResult: FlowValidationResult = {
      isValid: true,
      errors: [],
      nodeErrors: [],
    }

    const startNodes = nodes.filter((node) => node.type === BuilderNode.START)
    const endNodes = nodes.filter((node) => node.type === BuilderNode.END)
    const nonTerminalNodes = nodes.filter(
      (node) => node.type !== BuilderNode.START && node.type !== BuilderNode.END
    )

    if (startNodes.length === 0) {
      validationResult.errors.push(
        'O fluxo deve ter pelo menos um nó de início'
      )
    }

    if (startNodes.length > 1) {
      validationResult.errors.push('O fluxo deve ter apenas um nó de início')
    }

    if (endNodes.length === 0) {
      validationResult.errors.push('O fluxo deve ter pelo menos um nó de fim')
    }

    if (nonTerminalNodes.length === 0) {
      validationResult.errors.push(
        'O fluxo deve ter pelo menos um nó além de início e fim'
      )
    }

    let isStartConnected = false
    let isEndConnected = false
    const nodesWithEmptyTarget: Node[] = []

    for (const node of nodes) {
      const { outgoingEdges, incomingEdges } = findEdges(node, connectedEdges)

      if (node.type === BuilderNode.START) {
        isStartConnected = checkFlowTerminalNode(
          outgoingEdges,
          incomingEdges,
          BuilderNode.START
        )

        if (!isStartConnected) {
          validationResult.errors.push(
            'O nó de início deve estar conectado a pelo menos um outro nó'
          )
        }
      } else if (node.type === BuilderNode.END) {
        isEndConnected = checkFlowTerminalNode(
          outgoingEdges,
          incomingEdges,
          BuilderNode.END
        )

        if (!isEndConnected && endNodes.length > 0) {
          validationResult.errors.push(
            'O nó de fim deve estar conectado por pelo menos um outro nó'
          )
        }
      }

      if (
        checkLoneNode(
          outgoingEdges,
          incomingEdges,
          node.type as BuilderNodeType
        )
      ) {
        nodesWithEmptyTarget.push(node)
        validationResult.errors.push(
          `Nó "${node.type}" está desconectado do fluxo`
        )
      }

      const nodeErrors = validateNodeData(node)
      validationResult.nodeErrors.push(...nodeErrors)
    }

    const nodeIds = new Set(nodes.map((node) => node.id))
    const orphanedEdges = edges.filter(
      (edge) => !nodeIds.has(edge.source) || !nodeIds.has(edge.target)
    )

    if (orphanedEdges.length > 0) {
      validationResult.errors.push('Existem conexões órfãs no fluxo')
    }

    validationResult.isValid =
      validationResult.errors.length === 0 &&
      validationResult.nodeErrors.length === 0

    validationActions.setValidationResult(validationResult)

    onValidate?.(validationResult.isValid, validationResult)

    setIsValidating(false)
  }, [getNodes, getEdges, onValidate, validationActions])

  return [isValidating, validate]
}
